import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Autocomplete,
  Box,
  Chip,
  Alert,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from "@mui/material";
import { SubjectFormData, useSubjectMarksMappingByClassName } from "../../hooks/useSubjects";
import type { SubjectMarksMapping } from "../../hooks/useSubjectMarkMapping";
import {
  getBaseClassName,
  getSubjectMarksMappingByClassName,
  useSubjectMarkMapping,
} from "../../hooks/useSubjectMarkMapping";
import { useError } from "../../contexts/ErrorContext";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSessionClass } from "contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import {
  CLASS_1_TO_5_SUBJECTS,
  CLASS_6_TO_8_SUBJECTS,
  TENTH_CLASS_SUBJECTS,
  ELEVENTH_TWELFTH_CLASS_SUBJECTS,
  ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED,
  CLASS_Nursery_SUBJECTS,
  CLASS_LKG_SUBJECTS,
  CLASS_UKG_SUBJECTS,
} from "../enum/SubjectOptions";

interface SubjectFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: SubjectFormData;
  initialMappingData?: SubjectMarksMapping;
  onSubmit: (formData: SubjectFormData, mappingData?: SubjectMarksMapping) => void;
  onSubmitBulk?: (subjects: Array<SubjectFormData & { mappingData?: SubjectMarksMapping }>) => void;
  isEdit?: boolean;
  isBulkMode?: boolean;
  /** If true, allow editing only marks mapping; lock name/code/description/category. */
  lockSubjectCoreFields?: boolean;
  /** If true, lock everything (read-only modal). */
  lockAllFields?: boolean;
  existingSubjects?: string[]; // Array of subject codes that already exist
  isLoading?: boolean; // Loading state for subjects data
  isSubmitting?: boolean; // Loading state for creating/updating subjects
}

/** Marks fields: digits only; never store NaN from pasted/typed letters. */
function sanitizeMarksDigitsInput(raw: string): "" | number {
  const digits = raw.replace(/\D/g, "");
  if (digits === "") return "";
  const n = Number(digits);
  return Number.isFinite(n) ? n : "";
}

function normalizeMarksMappingComparable(m: SubjectMarksMapping) {
  return {
    subjectType: String(m.subjectType || ""),
    groupName: String(m.groupName || ""),
    totalMarks: Number(m.totalMarks),
    theoryMarks: Number(m.theoryMarks),
    practicalMarks: Number(m.practicalMarks),
    thPassMarks: Number(m.thPassMarks),
    prPassMarks: Number(m.prPassMarks),
    isAllowedGrace: Boolean(m.isAllowedGrace),
    grace: m.isAllowedGrace ? Number(m.grace ?? 0) : 0,
    e1Th: Number(m.e1Th),
    e1Pr: Number(m.e1Pr),
    e2Th: Number(m.e2Th),
    e2Pr: Number(m.e2Pr),
  };
}

function subjectMarksMappingComparableEqual(a: SubjectMarksMapping, b: SubjectMarksMapping): boolean {
  const A = normalizeMarksMappingComparable(a);
  const B = normalizeMarksMappingComparable(b);
  return (Object.keys(A) as (keyof typeof A)[]).every((k) => A[k] === B[k]);
}

/** JSON / Java may send `id` as string; strict `typeof === "number"` breaks PUT vs POST. */
function resolveMappingEntityId(id: unknown): number | undefined {
  if (id === null || id === undefined || id === "") return undefined;
  const n = typeof id === "number" ? id : Number(id);
  return Number.isFinite(n) ? n : undefined;
}

const SubjectFormModal: React.FC<SubjectFormModalProps> = ({
  open,
  handleClose,
  initialData,
  initialMappingData,
  onSubmit,
  onSubmitBulk,
  isEdit = false,
  isBulkMode = false,
  lockSubjectCoreFields = false,
  lockAllFields = false,
  existingSubjects = [],
  isLoading = false,
  isSubmitting = false,
}) => {
  const { selectedSession, selectedClass, selectedClassName } =
    useSessionClass();

  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useError();
  const {
    addSubjectMarkMappingMutation,
    updateSubjectMarkMappingMutation,
  } = useSubjectMarkMapping();

  const defaultSubjectCategory = "OTHER";
  const [formData, setFormData] = useState<SubjectFormData>(() => ({
    name: "",
    subjectCode: "",
    description: "",
    classId: selectedClass?.id || 0,
    className: selectedClassName,
    active: true,
    subjectCategory: defaultSubjectCategory,
    schoolId: user?.schoolId || 0,
    sessionId: selectedSession?.id,
  }));
  const [mappingData, setMappingData] = useState<SubjectMarksMapping>({
    className: selectedClassName || "",
    subjectCode: "",
    subjectName: "",
    subjectType: defaultSubjectCategory,
    groupName: "",
    totalMarks: 100,
    theoryMarks: 80,
    practicalMarks: 20,
    thPassMarks: 27,
    prPassMarks: 7,
    isAllowedGrace: false,
    grace: 0,
    e1Th: 27,
    e1Pr: 7,
    e2Th: 14,
    e2Pr: 4,
  });

  // Bulk per-subject mapping overrides (keyed by subjectCode)
  const [bulkMappingByCode, setBulkMappingByCode] = useState<Record<string, SubjectMarksMapping>>({});
  const [bulkMappingEditorOpen, setBulkMappingEditorOpen] = useState(false);
  const [bulkEditing, setBulkEditing] = useState<{ subjectName: string; subjectCode: string } | null>(null);
  const [bulkDraft, setBulkDraft] = useState<SubjectMarksMapping | null>(null);
  const [savingBulkMapping, setSavingBulkMapping] = useState(false);
  const bulkEditorServerAtOpenRef = useRef<SubjectMarksMapping | null>(null);

  const baseClassNameForMapping = useMemo(
    () => getBaseClassName(selectedClassName),
    [selectedClassName]
  );
  const subjectMarksMappingQuery = useSubjectMarksMappingByClassName(
    baseClassNameForMapping || undefined,
    false
  );

  const mappingByCode = useMemo(() => {
    const map = new Map<string, SubjectMarksMapping>();
    (subjectMarksMappingQuery.data || []).forEach((m) => {
      const code = String(m?.subjectCode || "").trim();
      if (code) map.set(code.toLowerCase(), m);
    });
    return map;
  }, [subjectMarksMappingQuery.data]);

  const mappingByName = useMemo(() => {
    const map = new Map<string, SubjectMarksMapping>();
    (subjectMarksMappingQuery.data || []).forEach((m) => {
      const name = String(m?.subjectName || "").trim();
      if (name) map.set(name.toLowerCase(), m);
    });
    return map;
  }, [subjectMarksMappingQuery.data]);

  const getSubjectCodeForDisplayName = (subjectName: string): string => {
    const byName = mappingByName.get(String(subjectName || "").trim().toLowerCase());
    if (byName?.subjectCode) return String(byName.subjectCode).trim();

    // 11th/12th can be "Name (code)"
    const match = String(subjectName || "").match(/\(([^)]+)\)\s*$/);
    if (match) return String(match[1] || "").trim();

    const fromList = (list: any[]) => {
      const found = list.find((s) => String(s?.name || "").trim() === String(subjectName || "").trim());
      return String(found?.code || "").trim();
    };

    if (isNinthOrTenthClass) return fromList(TENTH_CLASS_SUBJECTS as any[]);
    if (isPrePrimary) {
      const prePrimarySubjects = isNursery ? CLASS_Nursery_SUBJECTS : (isLKG ? CLASS_LKG_SUBJECTS : CLASS_UKG_SUBJECTS);
      return fromList(prePrimarySubjects as any[]);
    }
    if (isLowerClass) return fromList([...CLASS_1_TO_5_SUBJECTS, ...CLASS_6_TO_8_SUBJECTS] as any[]);
    return "";
  };

  const buildDefaultMapping = (subjectName: string, subjectCode: string): SubjectMarksMapping => {
    const total = 100;
    const theory = 80;
    const practical = 20;
    const thPass = Math.ceil(theory * 0.33);
    const prPass = Math.ceil(practical * 0.33);
    return {
      className: selectedClassName || "",
      subjectCode,
      subjectName,
      subjectType: getDefaultTypeBySubject(subjectName, subjectCode),
      groupName: "",
      totalMarks: total,
      theoryMarks: theory,
      practicalMarks: practical,
      thPassMarks: thPass,
      prPassMarks: prPass,
      isAllowedGrace: false,
      grace: 0,
      e1Th: thPass,
      e1Pr: prPass,
      e2Th: Math.ceil(thPass / 2),
      e2Pr: Math.ceil(prPass / 2),
    };
  };

  const openBulkEditor = (subjectName: string) => {
    const subjectCode = getSubjectCodeForDisplayName(subjectName);
    if (!subjectCode) return;

    const codeKey = subjectCode.trim().toLowerCase();
    const existingOverride = bulkMappingByCode[subjectCode] ?? bulkMappingByCode[codeKey];
    const existingServer = mappingByCode.get(codeKey);
    const draft = existingOverride || existingServer || buildDefaultMapping(subjectName, subjectCode);

    bulkEditorServerAtOpenRef.current = existingServer ? { ...existingServer } : null;

    setBulkEditing({ subjectName, subjectCode });
    setBulkDraft(draft);
    setBulkMappingEditorOpen(true);
  };

  const buildBulkMappingApiPayload = (
    draft: SubjectMarksMapping,
    subjectCode: string,
    subjectName: string
  ): SubjectMarksMapping => {
    const toNum = (v: unknown, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    return {
      ...draft,
      className: selectedClassName || "",
      subjectCode,
      subjectName,
      subjectType: draft.subjectType || defaultSubjectCategory,
      groupName: draft.groupName || "",
      totalMarks: toNum(draft.totalMarks, 100),
      theoryMarks: toNum(draft.theoryMarks, 80),
      practicalMarks: toNum(draft.practicalMarks, 20),
      thPassMarks: toNum(draft.thPassMarks, 0),
      prPassMarks: toNum(draft.prPassMarks, 0),
      isAllowedGrace: Boolean(draft.isAllowedGrace),
      grace: draft.isAllowedGrace ? toNum(draft.grace, 0) : 0,
      e1Th: toNum(draft.e1Th, 0),
      e1Pr: toNum(draft.e1Pr, 0),
      e2Th: toNum(draft.e2Th, 0),
      e2Pr: toNum(draft.e2Pr, 0),
    };
  };

  const saveBulkEditor = async () => {
    if (!bulkEditing || !bulkDraft || !bulkMarksSumValid) {
      if (!bulkDraft) setBulkMappingEditorOpen(false);
      return;
    }

    const subjectCode = bulkEditing.subjectCode;
    const codeNorm = subjectCode.trim().toLowerCase();

    const payload = buildBulkMappingApiPayload(bulkDraft, subjectCode, bulkEditing.subjectName);

    const rowFromCache = mappingByCode.get(codeNorm);
    const refRow = bulkEditorServerAtOpenRef.current;
    const rowFromRef =
      refRow && String(refRow.subjectCode || "").trim().toLowerCase() === codeNorm ? refRow : null;
    const rowFromBulk =
      bulkMappingByCode[subjectCode] ??
      bulkMappingByCode[codeNorm] ??
      undefined;

    let serverBaseline: SubjectMarksMapping | null =
      rowFromCache || rowFromRef || (rowFromBulk && resolveMappingEntityId(rowFromBulk.id) != null ? rowFromBulk : null);

    let mappingId =
      resolveMappingEntityId(bulkDraft.id) ??
      resolveMappingEntityId(serverBaseline?.id) ??
      resolveMappingEntityId(rowFromBulk?.id);

    try {
      setSavingBulkMapping(true);

      if (mappingId == null && baseClassNameForMapping) {
        const list = await getSubjectMarksMappingByClassName(baseClassNameForMapping);
        const found = list.find(
          (m) => String(m.subjectCode || "").trim().toLowerCase() === codeNorm
        );
        if (found) {
          serverBaseline = found;
          mappingId = resolveMappingEntityId(found.id);
        }
      }

      if (mappingId != null) {
        const baseline = serverBaseline ?? rowFromBulk ?? bulkDraft;
        const putBody: SubjectMarksMapping = {
          ...baseline,
          ...payload,
          id: mappingId,
        };

        if (subjectMarksMappingComparableEqual(putBody, baseline)) {
          setBulkMappingByCode((prev) => ({
            ...prev,
            [subjectCode]: putBody,
          }));
          setBulkMappingEditorOpen(false);
          return;
        }

        const updated = await updateSubjectMarkMappingMutation.mutateAsync(putBody);
        setBulkMappingByCode((prev) => ({
          ...prev,
          [subjectCode]: updated,
        }));
      } else {
        const created = await addSubjectMarkMappingMutation.mutateAsync(payload);
        setBulkMappingByCode((prev) => ({
          ...prev,
          [subjectCode]: created,
        }));
      }

      setBulkMappingEditorOpen(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const backendMessage = err?.response?.data?.message;
      showError(backendMessage || t("error_saving_subject_marks_mapping") || "Failed to save subject marks mapping.");
    } finally {
      setSavingBulkMapping(false);
    }
  };

  const bulkMarksSumValid = useMemo(() => {
    if (!bulkDraft) return true;
    const th = Number((bulkDraft as any).theoryMarks ?? 0);
    const pr = Number((bulkDraft as any).practicalMarks ?? 0);
    const total = Number((bulkDraft as any).totalMarks ?? 0);
    return th + pr === total;
  }, [bulkDraft]);

  const handleBulkDraftField = (name: keyof SubjectMarksMapping, value: string | number | boolean) => {
    setBulkDraft((prev) => {
      if (!prev) return prev;

      if (name === "isAllowedGrace") {
        const checked = Boolean(value);
        return checked ? { ...prev, isAllowedGrace: true } : { ...prev, isAllowedGrace: false, grace: 0 };
      }

      const numericFields: Array<keyof SubjectMarksMapping> = [
        "totalMarks",
        "theoryMarks",
        "practicalMarks",
        "thPassMarks",
        "prPassMarks",
        "grace",
        "e1Th",
        "e1Pr",
        "e2Th",
        "e2Pr",
      ];

      if (numericFields.includes(name)) {
        const n = typeof value === "number" ? value : Number(value || 0);
        return { ...prev, [name]: Number.isFinite(n) ? n : 0 } as SubjectMarksMapping;
      }

      return { ...prev, [name]: value } as SubjectMarksMapping;
    });
  };

  // Bulk mode state
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]); // For 11th/12th class groups
  const [isActive] = useState<boolean>(true);

  // Check class type for proper subject filtering
  const selectedClassNumber = selectedClassName?.match(/\d+/)?.[0];
  const isNinthOrTenthClass = selectedClassNumber === "9" || selectedClassNumber === "10";
  const isEleventhTwelfthClass = selectedClassNumber === "11" || selectedClassNumber === "12";
  const isLowerClass = selectedClassNumber ? Number(selectedClassNumber) >= 1 && Number(selectedClassNumber) <= 8 : false;

  const SUBJECT_TYPE_OPTIONS = React.useMemo(() => {
    const base = [
      { label: t("vocational") || "Vocational", value: "VOCATIONAL" as const },
      { label: t("others") || "Other", value: "OTHER" as const },
    ];

    // Practical type is only allowed for 11th/12th.
    if (isEleventhTwelfthClass) {
      return [...base, { label: "Practical", value: "PRACTICAL" as const }];
    }

    return base;
  }, [t, isEleventhTwelfthClass]);

  const isNursery = selectedClassName?.toLowerCase().includes("nursery");
  const isLKG = selectedClassName?.toLowerCase().includes("lkg");
  const isUKG = selectedClassName?.toLowerCase().includes("ukg");
  const isPrePrimary = isNursery || isLKG || isUKG;

  const getLowerClassSubjectList = React.useCallback(() => {
    if (!selectedClassNumber) return [];
    const classNum = Number(selectedClassNumber);
    if (classNum >= 1 && classNum <= 5) {
      return CLASS_1_TO_5_SUBJECTS;
    }
    if (classNum >= 6 && classNum <= 8) {
      return CLASS_6_TO_8_SUBJECTS;
    }
    return [];
  }, [selectedClassNumber]);

  // Get subject options based on class, filtering out existing subjects
  const getSubjectOptions = React.useMemo(() => {

    if (!selectedClassName) {
      return [];
    }

    // Handle case where existingSubjects might be undefined or null
    const safeExistingSubjects = existingSubjects || [];

    let subjects: string[] = [];

    if (isNinthOrTenthClass) {
      // For Class 9th and 10th, ALWAYS start with only the predefined subjects
      // This ensures no extra subjects can ever appear
      subjects = TENTH_CLASS_SUBJECTS.map(subject => subject.name);

      // Filter out subjects that already exist in the database (across all Class 10th sections)
      // Only filter from the predefined list, ensuring no external subjects are considered
      // For 9th/10th, check by code (existingSubjects now contains codes)
      subjects = subjects.filter(subjectName => {
        const subject = TENTH_CLASS_SUBJECTS.find(s => s.name === subjectName);
        if (subject && subject.code) {
          return !safeExistingSubjects.includes(subject.code);
        }
        // Fallback: if no code found, check by name for backward compatibility
        return !safeExistingSubjects.includes(subjectName);
      });

    } else if (isEleventhTwelfthClass) {
      // For Class 11th/12th, use name+code combination for display, but filter by code
      subjects = ELEVENTH_TWELFTH_CLASS_SUBJECTS.map(subject => `${subject.name} (${subject.code})`);

      // Filter out subjects whose code already exists
      subjects = subjects.filter(subjectDisplay => {
        const match = subjectDisplay.match(/\((\d+)\)$/);
        if (match) {
          const code = match[1];
          return !safeExistingSubjects.includes(code);
        }
        return true;
      });

    } else if (isPrePrimary) {
      const prePrimarySubjects = isNursery ? CLASS_Nursery_SUBJECTS : (isLKG ? CLASS_LKG_SUBJECTS : CLASS_UKG_SUBJECTS);
      subjects = prePrimarySubjects
        .filter(subject => !safeExistingSubjects.includes(subject.code))
        .map(subject => subject.name);
    } else if (isLowerClass) {
      const lowerClassSubjects = getLowerClassSubjectList();
      subjects = lowerClassSubjects
        .filter(subject => !safeExistingSubjects.includes(subject.code))
        .map(subject => subject.name);
    }

    // In bulk mode, also filter out currently selected subjects
    if (isBulkMode) {
      subjects = subjects.filter(subject => !selectedSubjects.includes(subject));
    }

    return subjects;
  }, [isNinthOrTenthClass, isEleventhTwelfthClass, isLowerClass, isPrePrimary, isNursery, isLKG, existingSubjects, isBulkMode, selectedSubjects, selectedClassName, getLowerClassSubjectList]);

  const getAvailableGroups = React.useMemo(() => {
    if (!isEleventhTwelfthClass || !selectedClassName) {
      return [];
    }

    const safeExistingSubjects = existingSubjects || [];

    return ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.map(group => {
      // Filter out subjects that already exist (check by code only)
      const availableSubjects = group.subjects.filter(
        subject => !safeExistingSubjects.includes(subject.code)
      );

      return {
        ...group,
        availableSubjects,
        allSubjectsExist: availableSubjects.length === 0,
      };
    }).filter(group => !group.allSubjectsExist);
  }, [isEleventhTwelfthClass, selectedClassName, existingSubjects]);

  // Handle subject name change for all classes
  const handleSubjectNameChange = (event: any, newValue: string | null) => {
    if (!newValue) {
      setFormData((prev) => ({
        ...prev,
        name: "",
        subjectCode: "",
      }));
      return;
    }

    // Auto-populate subject code based on selected subject name
    if (isNinthOrTenthClass) {
      const selectedSubject = TENTH_CLASS_SUBJECTS.find(subject => subject.name === newValue);
      setFormData((prev) => ({
        ...prev,
        name: newValue,
        subjectCode: selectedSubject?.code || prev.subjectCode || "",
      }));
      return;
    }

    if (isLowerClass) {
      const lowerClassSubjects = getLowerClassSubjectList();
      const selectedSubject = lowerClassSubjects.find(subject => subject.name === newValue);
      setFormData((prev) => ({
        ...prev,
        name: newValue,
        subjectCode: selectedSubject?.code || prev.subjectCode || "",
      }));
      return;
    }

    if (isPrePrimary) {
      const prePrimarySubjects = isNursery ? CLASS_Nursery_SUBJECTS : (isLKG ? CLASS_LKG_SUBJECTS : CLASS_UKG_SUBJECTS);
      const selectedSubject = prePrimarySubjects.find(subject => subject.name === newValue);
      setFormData((prev) => ({
        ...prev,
        name: newValue,
        subjectCode: selectedSubject?.code || prev.subjectCode || "",
      }));
      return;
    }

    if (isEleventhTwelfthClass) {
      // Handle format "name (code)" for Class 11th/12th
      let subjectName = newValue;
      let subjectCode = "";

      // Check if the format is "name (code)"
      const match = newValue.match(/^(.+?)\s*\((\d+)\)$/);
      if (match) {
        subjectName = match[1].trim();
        subjectCode = match[2];
      } else {
        // Fallback: try to find by name only
        const selectedSubject = ELEVENTH_TWELFTH_CLASS_SUBJECTS.find(subject => subject.name === newValue);
        subjectCode = selectedSubject?.code || "";
      }

      setFormData((prev) => ({
        ...prev,
        name: subjectName,
        subjectCode: subjectCode || prev.subjectCode || "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      name: newValue,
      subjectCode:
        prev.subjectCode && prev.name === newValue
          ? prev.subjectCode
          : newValue.substring(0, 3).toUpperCase() + Math.floor(Math.random() * 100).toString().padStart(2, "0"),
    }));
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        subjectCode: initialData.subjectCode || "",
        description: initialData.description || "",
        classId: initialData.classId ?? selectedClass?.id ?? 0,
        className: initialData.className ?? selectedClassName,
        active: initialData.active ?? true,
        subjectCategory: initialData.subjectCategory || defaultSubjectCategory,
        schoolId: initialData.schoolId ?? (user?.schoolId || 0),
        sessionId: initialData.sessionId ?? selectedSession?.id,
      });
      if (initialMappingData) {
        setMappingData({
          ...initialMappingData,
          className: initialData.className ?? selectedClassName ?? initialMappingData.className,
          subjectCode: initialData.subjectCode || initialMappingData.subjectCode || "",
          subjectName: initialData.name || initialMappingData.subjectName || "",
          subjectType: initialMappingData.subjectType || defaultSubjectCategory,
          groupName: initialMappingData.groupName || "",
          totalMarks: Number((initialMappingData as any).totalMarks ?? 100),
          theoryMarks: Number((initialMappingData as any).theoryMarks ?? 80),
          practicalMarks: Number((initialMappingData as any).practicalMarks ?? 20),
          thPassMarks: Number((initialMappingData as any).thPassMarks ?? 27),
          prPassMarks: Number((initialMappingData as any).prPassMarks ?? 7),
          isAllowedGrace: Boolean(initialMappingData.isAllowedGrace),
          grace: Number((initialMappingData as any).grace ?? 0),
          e1Th: Number((initialMappingData as any).e1Th ?? 27),
          e1Pr: Number((initialMappingData as any).e1Pr ?? 7),
          e2Th: Number((initialMappingData as any).e2Th ?? 14),
          e2Pr: Number((initialMappingData as any).e2Pr ?? 4),
        });
      } else {
        setMappingData((prev) => ({
          ...prev,
          className: initialData.className ?? selectedClassName,
          subjectCode: initialData.subjectCode || "",
          subjectName: initialData.name || "",
        }));
      }
    } else {
      setFormData({
        name: "",
        subjectCode: "",
        description: "",
        classId: selectedClass?.id || 0,
        className: selectedClassName,
        active: true,
        subjectCategory: defaultSubjectCategory,
        schoolId: user?.schoolId || 0,
        sessionId: selectedSession?.id,
      });
      setMappingData({
        className: selectedClassName || "",
        subjectCode: "",
        subjectName: "",
        subjectType: defaultSubjectCategory,
        groupName: "",
        totalMarks: 100,
        theoryMarks: 80,
        practicalMarks: 20,
        thPassMarks: 27,
        prPassMarks: 7,
        isAllowedGrace: false,
        grace: 0,
        e1Th: 27,
        e1Pr: 7,
        e2Th: 14,
        e2Pr: 4,
      });
    }
  }, [initialData, initialMappingData, selectedClassName, selectedClass, selectedSession?.id, user?.schoolId, open, defaultSubjectCategory]);

  // Reset selected subjects when existingSubjects changes (e.g., when switching sections)
  useEffect(() => {
    if (isBulkMode) {
      setSelectedSubjects([]);
      setSelectedGroups([]);
    }
  }, [existingSubjects, isBulkMode, defaultSubjectCategory]);

  // Reset selected subjects when class changes in bulk mode
  useEffect(() => {
    if (isBulkMode) {
      setSelectedSubjects([]);
      setSelectedGroups([]);
    }
  }, [selectedClassName, isBulkMode, defaultSubjectCategory]);

  // Reset form when class changes (but keep subject code if user has entered one)
  useEffect(() => {
    if (!isEdit) {
      setFormData((prev) => ({
        ...prev,
        name: "",
        // Only clear subject code if it was auto-populated, not if user manually entered it
        subjectCode: prev.subjectCode && !isNinthOrTenthClass ? prev.subjectCode : "",
      }));
    }
  }, [selectedClassName, isEdit, isNinthOrTenthClass]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target as HTMLInputElement;

    // Remove only leading spaces
    const cleanedValue = value.replace(/^\s+/, "");

    setFormData((prev) => ({
      ...prev,
      [name!]: cleanedValue,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent submission if no session or class is selected
    if (!selectedSession || !selectedClass) {
      return;
    }

    // Prevent submission if session is inactive
    if (selectedSession && !selectedSession.isActive) {
      return;
    }

    const normalizedSubjectType = String(
      mappingData.subjectType || formData.subjectCategory || defaultSubjectCategory
    ).toUpperCase();

    const toNumber = (v: any) => {
      if (v === "" || v === null || v === undefined) return 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const finalMappingData: SubjectMarksMapping = {
      ...mappingData,
      className: selectedClassName || formData.className || "",
      subjectCode: formData.subjectCode,
      subjectName: formData.name,
      subjectType: normalizedSubjectType,
      groupName: isEleventhTwelfthClass ? (mappingData.groupName || "") : "",
      totalMarks: toNumber((mappingData as any).totalMarks),
      theoryMarks: toNumber((mappingData as any).theoryMarks),
      practicalMarks: toNumber((mappingData as any).practicalMarks),
      thPassMarks: toNumber((mappingData as any).thPassMarks),
      prPassMarks: toNumber((mappingData as any).prPassMarks),
      e1Th: toNumber((mappingData as any).e1Th),
      e1Pr: toNumber((mappingData as any).e1Pr),
      e2Th: toNumber((mappingData as any).e2Th),
      e2Pr: toNumber((mappingData as any).e2Pr),
      grace: mappingData.isAllowedGrace ? toNumber((mappingData as any).grace) : 0,
    };

    const finalFormData: SubjectFormData = {
      ...formData,
      subjectCategory: normalizedSubjectType,
      sessionId: selectedSession.id,
    };

    onSubmit(finalFormData, finalMappingData);
  };

  const handleMappingInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const numericFields: Array<keyof SubjectMarksMapping> = [
      "totalMarks",
      "theoryMarks",
      "practicalMarks",
      "thPassMarks",
      "prPassMarks",
      "grace",
      "e1Th",
      "e1Pr",
      "e2Th",
      "e2Pr",
    ];

    setMappingData((prev) => ({
      ...prev,
      [name]:
        numericFields.includes(name as keyof SubjectMarksMapping)
          ? (sanitizeMarksDigitsInput(value) as any)
          : value,
    }));
  };

  const handleMappingSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setMappingData((prev) => {
      if (name === "isAllowedGrace" && !checked) {
        return { ...prev, isAllowedGrace: false, grace: 0 };
      }
      return { ...prev, [name]: checked } as SubjectMarksMapping;
    });
  };

  const handleSubjectTypeChange = (e: any) => {
    const value = String(e.target.value || defaultSubjectCategory).toUpperCase();
    setMappingData((prev) => ({ ...prev, subjectType: value }));
    setFormData((prev) => ({ ...prev, subjectCategory: value }));
  };

  const marksSumValid = React.useMemo(() => {
    const rawT = mappingData.totalMarks as unknown;
    const rawTh = mappingData.theoryMarks as unknown;
    const rawPr = mappingData.practicalMarks as unknown;
    if (rawT === "" || rawTh === "" || rawPr === "") return false;

    const total = Number(rawT);
    const th = Number(rawTh);
    const pr = Number(rawPr);

    if (!Number.isFinite(total) || !Number.isFinite(th) || !Number.isFinite(pr)) {
      return false;
    }

    return th + pr === total;
  }, [mappingData.totalMarks, mappingData.theoryMarks, mappingData.practicalMarks]);

  const normalizeSubjectType = (value?: string): string => {
    const normalized = String(value || "").toUpperCase();
    if (normalized === "VOCATIONAL" || normalized === "PRACTICAL" || normalized === "OTHER") {
      return normalized;
    }
    return defaultSubjectCategory;
  };

  const getDefaultTypeBySubject = (subjectName: string, subjectCode?: string): string => {
    const name = String(subjectName || "").trim().toLowerCase();
    const code = String(subjectCode || "").trim();

    const fromList = (list: Array<{ name: string; code: string; type?: string }>) => {
      const byCode = code ? list.find((s) => String(s.code).trim() === code) : undefined;
      if (byCode?.type) return normalizeSubjectType(byCode.type);

      const byName = list.find((s) => String(s.name).trim().toLowerCase() === name);
      if (byName?.type) return normalizeSubjectType(byName.type);

      return undefined;
    };

    const prePrimarySubjects = [
      ...CLASS_Nursery_SUBJECTS,
      ...CLASS_LKG_SUBJECTS,
      ...CLASS_UKG_SUBJECTS,
    ];
    const lowerSubjects = [...CLASS_1_TO_5_SUBJECTS, ...CLASS_6_TO_8_SUBJECTS];
    const eleventhTwelfthSubjects = ELEVENTH_TWELFTH_CLASS_SUBJECTS as Array<{
      name: string;
      code: string;
      type?: string;
    }>;

    return (
      fromList(prePrimarySubjects as Array<{ name: string; code: string; type?: string }>) ||
      fromList(lowerSubjects as Array<{ name: string; code: string; type?: string }>) ||
      fromList(TENTH_CLASS_SUBJECTS as Array<{ name: string; code: string; type?: string }>) ||
      fromList(eleventhTwelfthSubjects) ||
      defaultSubjectCategory
    );
  };

  // Bulk mode handlers
  const handleSubjectToggle = (subjectName: string) => {
    setSelectedSubjects(prev => {
      const isRemoving = prev.includes(subjectName);
      const newSelection = isRemoving
        ? prev.filter(name => name !== subjectName)
        : [...prev, subjectName];

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (isEleventhTwelfthClass) {
      const allGroups = getAvailableGroups.map(group => group.groupName);
      setSelectedGroups(prev => [...prev, ...allGroups.filter(group => !prev.includes(group))]);
    } else {
      const allSubjects = getSubjectOptions;
      setSelectedSubjects(prev => [...prev, ...allSubjects.filter(subject => !prev.includes(subject))]);
    }
  };

  const handleDeselectAll = () => {
    setSelectedSubjects([]);
    setSelectedGroups([]);
  };

  const handleGroupToggle = (groupName: string) => {
    setSelectedGroups(prev => {
      const isRemoving = prev.includes(groupName);
      const newSelection = isRemoving
        ? prev.filter(name => name !== groupName)
        : [...prev, groupName];

      return newSelection;
    });
  };

  const handleBulkSubmit = () => {
    // Prevent submission if no session or class is selected
    if (!selectedSession || !selectedClass) {
      return;
    }

    // Prevent submission if session is inactive
    if (selectedSession && !selectedSession.isActive) {
      return;
    }

    let bulkSubjects: Array<SubjectFormData & { mappingData?: SubjectMarksMapping }> = [];

    if (isEleventhTwelfthClass && selectedGroups.length > 0) {
      const subjectsMap = new Map<string, { subject: { name: string; code: string; type?: string }; groups: string[] }>();
      const safeExistingSubjects = existingSubjects || [];

      selectedGroups.forEach(groupName => {
        const group = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === groupName);
        if (group) {
          // Collect ALL subjects from the group, including all subgroups
          let allGroupSubjects = [...group.subjects]; // Start with main subjects array

          // If group has subgroups, also collect subjects from all subgroups
          if (group.hasSubgroups && group.subgroups) {
            group.subgroups.forEach(subgroup => {
              subgroup.subjects.forEach(subgroupSubject => {
                // Add subject if not already in the list (by code)
                const alreadyIncluded = allGroupSubjects.some(s => s.code === subgroupSubject.code);
                if (!alreadyIncluded) {
                  allGroupSubjects.push(subgroupSubject);
                }
              });
            });
          }

          // Filter out subjects that already exist by code to avoid duplicate errors
          const availableSubjects = allGroupSubjects.filter(
            subject => !safeExistingSubjects.includes(subject.code)
          );

          // Process only available subjects (skip those that already exist by code)
          availableSubjects.forEach(subject => {
            const existingEntry = subjectsMap.get(subject.code);
            if (existingEntry) {
              if (!existingEntry.groups.includes(groupName)) {
                existingEntry.groups.push(groupName);
              }
            } else {
              // New subject, add it to the map
              subjectsMap.set(subject.code, {
                subject: { name: subject.name, code: subject.code, type: (subject as any).type },
                groups: [groupName]
              });
            }
          });
        }
      });

      subjectsMap.forEach(({ subject, groups }) => {
        const groupNames = groups.join(", ");
        const resolvedType = getDefaultTypeBySubject(subject.name, subject.code);
        const mappingFromServer = mappingByCode.get(String(subject.code).trim().toLowerCase());
        const mappingOverride = bulkMappingByCode[subject.code];
        bulkSubjects.push({
          name: subject.name,
          subjectCode: subject.code,
          description: groups.length > 1
            ? `${subject.name} for ${selectedClassName} (${groupNames})`
            : `${subject.name} for ${selectedClassName} (${groups[0]})`,
          classId: selectedClass?.id || 0,
          className: selectedClassName,
          active: isActive,
          subjectCategory: resolvedType,
          schoolId: user?.schoolId || 0,
          sessionId: selectedSession?.id ?? 0,
          mappingData: mappingOverride || mappingFromServer || buildDefaultMapping(subject.name, subject.code),
        });
      });
    } else {
      if (selectedSubjects.length === 0) return;

      bulkSubjects = selectedSubjects.map(subjectInput => {
        // Handle format "name (code)" for Class 11th/12th
        let subjectName = subjectInput;
        let subjectCode = "";

        let resolvedType = defaultSubjectCategory;
        if (isEleventhTwelfthClass) {
          // Check if the format is "name (code)"
          const match = subjectInput.match(/^(.+?)\s*\((\d+)\)$/);
          if (match) {
            subjectName = match[1].trim();
            subjectCode = match[2];
          } else {
            // Fallback: try to find by name only
            const predefinedSubject = ELEVENTH_TWELFTH_CLASS_SUBJECTS.find(s => s.name === subjectInput);
            subjectCode = predefinedSubject?.code || "";
          }
          const predefinedSubject = ELEVENTH_TWELFTH_CLASS_SUBJECTS.find(
            (s) => String(s.code).trim() === String(subjectCode).trim()
          ) || ELEVENTH_TWELFTH_CLASS_SUBJECTS.find((s) => s.name === subjectName);
          resolvedType = normalizeSubjectType((predefinedSubject as any)?.type) || getDefaultTypeBySubject(subjectName, subjectCode);
        } else if (isNinthOrTenthClass) {
          const predefinedSubject = TENTH_CLASS_SUBJECTS.find(s => s.name === subjectInput);
          subjectCode = predefinedSubject?.code || subjectInput.substring(0, 3).toUpperCase() + Math.floor(Math.random() * 100).toString().padStart(2, '0');
          // For 9th/10th, prefer explicit enum type from `TENTH_CLASS_SUBJECTS`.
          resolvedType = normalizeSubjectType((predefinedSubject as any)?.type) || getDefaultTypeBySubject(subjectName, subjectCode);
        } else if (isPrePrimary) {
          const prePrimarySubjects = isNursery ? CLASS_Nursery_SUBJECTS : (isLKG ? CLASS_LKG_SUBJECTS : CLASS_UKG_SUBJECTS);
          const predefinedSubject = prePrimarySubjects.find(s => s.name === subjectInput);
          subjectCode = predefinedSubject?.code || subjectInput.substring(0, 3).toUpperCase() + Math.floor(Math.random() * 100).toString().padStart(2, '0');
          resolvedType = getDefaultTypeBySubject(subjectName, subjectCode);
        } else if (isLowerClass) {
          const lowerClassSubjects = getLowerClassSubjectList();
          const predefinedSubject = lowerClassSubjects.find(s => s.name === subjectInput);
          subjectCode = predefinedSubject?.code || subjectInput.substring(0, 3).toUpperCase() + Math.floor(Math.random() * 100).toString().padStart(2, '0');
          resolvedType = getDefaultTypeBySubject(subjectName, subjectCode);
        } else {
          // For other classes, generate a simple code
          subjectCode = subjectInput.substring(0, 3).toUpperCase() + Math.floor(Math.random() * 100).toString().padStart(2, '0');
          resolvedType = getDefaultTypeBySubject(subjectName, subjectCode);
        }

        const mappingFromServer = subjectCode
          ? mappingByCode.get(subjectCode.trim().toLowerCase())
          : undefined;
        const mappingOverride = subjectCode
          ? bulkMappingByCode[subjectCode] ?? bulkMappingByCode[subjectCode.trim().toLowerCase()]
          : undefined;
        const mappingDefault = subjectCode ? buildDefaultMapping(subjectName, subjectCode) : undefined;

        return {
          name: subjectName,
          subjectCode: subjectCode || subjectName.substring(0, 3).toUpperCase() + Math.floor(Math.random() * 100).toString().padStart(2, '0'),
          description: `${subjectName} for ${selectedClassName}`,
          classId: selectedClass?.id || 0,
          className: selectedClassName,
          active: isActive,
          subjectCategory: resolvedType,
          schoolId: user?.schoolId || 0,
          sessionId: selectedSession?.id ?? 0,
          mappingData: mappingOverride || mappingFromServer || mappingDefault,
        };
      });
    }

    if (onSubmitBulk && bulkSubjects.length > 0) {
      onSubmitBulk(bulkSubjects);
      // Clear selections after successful submission
      setSelectedSubjects([]);
      setSelectedGroups([]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {isBulkMode ? t("add_multiple_subjects") : (isEdit ? t("edit_subject") : t("add_subject"))}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {isBulkMode ? (
        <DialogContent>
          {(!selectedSession || !selectedClass || !selectedClassName) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("please_select_session_and_class_before_creating_subjects")}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              {isEleventhTwelfthClass
                ? `${t("available_groups_for") || "Available Groups for"} ${selectedClassName}`
                : `${t("available_subjects_for")} ${selectedClassName}`
              }
            </Typography>
            <Box>
              <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                {t("select_all")}
              </Button>
              <Button size="small" onClick={handleDeselectAll}>
                {t("deselect_all")}
              </Button>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            {(() => {
              if (isLoading || !selectedClassName) {
                return (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                      {!selectedClassName ? t("loading_class_selection") : t("loading_subjects")}
                    </Typography>
                  </Box>
                );
              } else if (isEleventhTwelfthClass) {
                // Show groups for 11th/12th class
                const availableGroups = getAvailableGroups.filter(
                  group => !selectedGroups.includes(group.groupName)
                );

                if (availableGroups.length > 0) {
                  return (
                    <Grid container spacing={2}>
                      {availableGroups.map((group, index) => (
                        <Grid item xs={12} sm={4} key={index}>
                          <Card
                            onClick={() => handleGroupToggle(group.groupName)}
                            sx={{
                              cursor: "pointer",
                              transition: "all 0.2s ease-in-out",
                              border: "2px solid",
                              borderColor: selectedGroups.includes(group.groupName) ? "primary.main" : "divider",
                              backgroundColor: selectedGroups.includes(group.groupName) ? "primary.light" : "background.paper",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: 4,
                                borderColor: "primary.main",
                              },
                              position: "relative",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1, pb: "16px !important" }}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="600"
                                    sx={{
                                      mb: 0.5,
                                      color: "text.primary",
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {group.groupName}
                                  </Typography>
                                  {group.description && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "0.7rem",
                                        display: "block",
                                        mb: 0.5,
                                      }}
                                    >
                                      {group.description}
                                    </Typography>
                                  )}
                                  {group.groupCode && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: "0.7rem",
                                        display: "block",
                                        mb: 0.5,
                                      }}
                                    >
                                      {t('group_code') || 'Group Code'}: {group.groupCode}
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.75rem",
                                      display: "block",
                                    }}
                                  >
                                    {group.availableSubjects.length} {t("subjects_available") || "subjects available"}
                                  </Typography>
                                </Box>
                                {selectedGroups.includes(group.groupName) && (
                                  <CheckCircleIcon
                                    sx={{
                                      color: "primary.main",
                                      fontSize: 24,
                                      ml: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  );
                } else {
                  return (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" sx={{ color: "text.secondary", mb: 1 }}>
                        {t("no_available_groups_to_select") || "No available groups to select"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {t("all_subjects_for_class_created")}
                      </Typography>
                    </Box>
                  );
                }
              } else if (getSubjectOptions.length > 0) {
                // Show individual subjects for other classes
                const availableSubjects = getSubjectOptions.filter(subjectName => !selectedSubjects.includes(subjectName));
                return (
                  <Grid container spacing={2}>
                    {availableSubjects.map((subjectName, index) => {
                      const subjectCode = getSubjectCodeForDisplayName(subjectName);
                      const mapped = subjectCode
                        ? bulkMappingByCode[subjectCode] ??
                          bulkMappingByCode[subjectCode.trim().toLowerCase()] ??
                          mappingByCode.get(subjectCode.trim().toLowerCase())
                        : undefined;
                      const total = mapped?.totalMarks;
                      const th = mapped?.theoryMarks;
                      const pr = mapped?.practicalMarks;

                      return (
                      <Grid item xs={12} sm={4} md={4} key={index}>
                        <Card
                          onClick={() => handleSubjectToggle(subjectName)}
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            border: "2px solid",
                            borderColor: selectedSubjects.includes(subjectName) ? "primary.main" : "divider",
                            backgroundColor: selectedSubjects.includes(subjectName) ? "primary.light" : "background.paper",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: 4,
                              borderColor: "primary.main",
                            },
                            position: "relative",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, pb: "16px !important" }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                              <Box sx={{ flex: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="600"
                                    sx={{
                                      mb: 0.5,
                                      color: "text.primary",
                                      lineHeight: 1.3,
                                      pr: 1,
                                    }}
                                  >
                                    {subjectName}
                                  </Typography>

                                  <IconButton
                                    size="small"
                                    disabled={!subjectCode}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openBulkEditor(subjectName);
                                    }}
                                    sx={{ mt: "-2px" }}
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Box>

                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary", fontSize: "0.66rem", lineHeight: 1.2 }}
                                >
                                  {subjectMarksMappingQuery.isLoading
                                    ? (t("loading") || "Loading") + "..."
                                    : `Total: ${total ?? 100} | TH: ${th ?? 80} | PR: ${pr ?? 20}`}
                                </Typography>
                              </Box>
                              {selectedSubjects.includes(subjectName) && (
                                <CheckCircleIcon
                                  sx={{
                                    color: "primary.main",
                                    fontSize: 24,
                                    ml: 1,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      );
                    })}
                  </Grid>
                );
              } else {
                return (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body1" sx={{ color: "text.secondary", mb: 1 }}>
                      {t("no_available_subjects_to_select")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {t("all_subjects_for_class_created")}
                    </Typography>
                  </Box>
                );
              }
            })()}
          </Box>

          {/* Selected Groups/Subjects Section */}
          {((isEleventhTwelfthClass && selectedGroups.length > 0) || (!isEleventhTwelfthClass && selectedSubjects.length > 0)) && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  {isEleventhTwelfthClass ? t("selected_groups") || "Selected Groups" : t("selected_subjects")}
                </Typography>
                <Chip
                  label={
                    isEleventhTwelfthClass
                      ? `${selectedGroups.length} ${t("groups_selected") || "groups selected"}`
                      : `${selectedSubjects.length} ${t("subjects_selected")}`
                  }
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                  minHeight: 60,
                  alignItems: "center",
                }}
              >
                {isEleventhTwelfthClass ? (
                  selectedGroups.map((groupName, index) => {
                    const group = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === groupName);
                    const safeExistingSubjects = existingSubjects || [];
                    const availableSubjects = group?.subjects.filter(
                      subject => !safeExistingSubjects.includes(subject.name)
                    ) || [];

                    return (
                      <Chip
                        key={index}
                        label={`${groupName} (${availableSubjects.length} ${t("subjects") || "subjects"})`}
                        onDelete={() => handleGroupToggle(groupName)}
                        color="primary"
                        variant="filled"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          height: 32,
                          "& .MuiChip-deleteIcon": {
                            fontSize: "1.1rem",
                            "&:hover": {
                              color: "error.main",
                            },
                          },
                        }}
                        icon={<CheckCircleIcon sx={{ fontSize: "1.1rem !important" }} />}
                      />
                    );
                  })
                ) : (
                  selectedSubjects.map((subjectName, index) => (
                    <Chip
                      key={index}
                      label={subjectName}
                      onDelete={() => handleSubjectToggle(subjectName)}
                      color="primary"
                      variant="filled"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        height: 32,
                        "& .MuiChip-deleteIcon": {
                          fontSize: "1.1rem",
                          "&:hover": {
                            color: "error.main",
                          },
                        },
                      }}
                      icon={<CheckCircleIcon sx={{ fontSize: "1.1rem !important" }} />}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}

          {/* 
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                name="active"
                color="primary"
              />
            }
            label={t("active")}
            sx={{ ml: 1 }}
          />
          */}
        </DialogContent>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {(!selectedSession || !selectedClass) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("please_select_session_and_class_before_creating_subjects")}
              </Alert>
            )}

            <Autocomplete
              freeSolo
              options={getSubjectOptions}
              value={formData.name}
              onChange={handleSubjectNameChange}
              onInputChange={(event, newInputValue) => {
                setFormData((prev) => ({
                  ...prev,
                  name: newInputValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("subject_name")}
                  required
                  margin="dense"
                  size="small"
                  variant="outlined"
                  disabled={lockAllFields || lockSubjectCoreFields}
                  InputLabelProps={{
                    sx: {
                      "& .MuiFormLabel-asterisk": { color: "red" },
                    },
                  }}
                  sx={{
                    backgroundColor: "#f8f9fb",
                  }}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                },
              }}
            />
            <TextField
              fullWidth
              label={t("subject_code")}
              name="subjectCode"
              value={formData.subjectCode}
              onChange={handleChange}
              margin="dense"
              size="small"
              variant="outlined"
              required
              disabled={lockAllFields || lockSubjectCoreFields}
              helperText={
                isNinthOrTenthClass ? t("code_auto_populated_predefined") : t("code_auto_suggested_subject_name")
              }
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": { color: "red" },
                },
              }}
              sx={{
                backgroundColor: "#f8f9fb",
              }}
            />
            <TextField
              fullWidth
              label={t("subject_description")}
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="dense"
              size="small"
              variant="outlined"
              disabled={lockAllFields || lockSubjectCoreFields}
              InputLabelProps={{
                sx: {
                  "& .MuiFormLabel-asterisk": { color: "red" },
                },
              }}
              sx={{
                backgroundColor: "#f8f9fb",
              }}
            />
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Subject Marks Mapping
                </Typography>
              </Grid>
              {/* Row 1: Subject Type + Total Marks */}
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  margin="dense"
                  required
                  variant="outlined"
                  size="small"
                  sx={{ backgroundColor: "#f8f9fb" }}
                >
                  <InputLabel
                    sx={{
                      "& .MuiFormLabel-asterisk": { color: "red" },
                    }}
                  >
                    Subject Type
                  </InputLabel>
                  <Select
                    value={mappingData.subjectType || defaultSubjectCategory}
                    label="Subject Type"
                    onChange={handleSubjectTypeChange}
                    size="small"
                    disabled={lockAllFields}
                    input={<OutlinedInput label="Subject Type" size="small" />}
                    sx={{
                      minHeight: "40px",
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  >
                    {SUBJECT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                  label="Total Marks"
                  name="totalMarks"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.totalMarks}
                  onChange={handleMappingInputChange}
                    margin="dense"
                    size="small"
                  required
                  error={!marksSumValid}
                  helperText={!marksSumValid ? "Theory Marks + Practical Marks must equal Total Marks." : " "}
                    sx={{ backgroundColor: "#f8f9fb" }}
                  />
                </Grid>

              {/* Optional: Group Name (11th/12th only) */}
              {isEleventhTwelfthClass && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Group Name"
                    name="groupName"
                    value={mappingData.groupName}
                    onChange={handleMappingInputChange}
                    margin="dense"
                    size="small"
                    sx={{ backgroundColor: "#f8f9fb" }}
                  />
                </Grid>
              )}

              {/* Row 2: Theory Marks + Practical Marks */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Theory Marks"
                  name="theoryMarks"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.theoryMarks}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  error={!marksSumValid}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Practical Marks"
                  name="practicalMarks"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.practicalMarks}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  error={!marksSumValid}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Row 3: Pass Marks */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Theory Pass Marks"
                  name="thPassMarks"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.thPassMarks}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Practical Pass Marks"
                  name="prPassMarks"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.prPassMarks}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Row 4: E1/E2 Grade marks */}
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="E1 Grade Theory"
                  name="e1Th"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.e1Th}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="E1 Grade Practical"
                  name="e1Pr"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.e1Pr}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="E2 Grade Theory"
                  name="e2Th"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.e2Th}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="E2 Grade Practical"
                  name="e2Pr"
                  type="text"
                  inputMode="numeric"
                  value={mappingData.e2Pr}
                  onChange={handleMappingInputChange}
                  margin="dense"
                  size="small"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Row 5: Is Allowed Grace + (conditional) Grace field */}
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mappingData.isAllowedGrace}
                      onChange={handleMappingSwitchChange}
                      name="isAllowedGrace"
                      color="primary"
                    />
                  }
                  label="Is Allowed Grace"
                  sx={{ ml: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                {mappingData.isAllowedGrace ? (
                  <TextField
                    fullWidth
                    label="Grace"
                    name="grace"
                    type="text"
                    inputMode="numeric"
                    value={mappingData.grace}
                    onChange={handleMappingInputChange}
                    margin="dense"
                    size="small"
                    required
                    sx={{ backgroundColor: "#f8f9fb" }}
                  />
                ) : (
                  <Box sx={{ height: 40 }} />
                )}
              </Grid>
            </Grid>
            {/* 
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleSwitchChange}
                  name="active"
                  color="primary"
                />
              }
              label={t("active")}
              sx={{ ml: 1 }}
            />
            */}
          </DialogContent>
        </form>
      )}
      <DialogActions sx={{ justifyContent: "flex-end", pb: 2, mr: 4 }}>
        {isBulkMode ? (
          <>
            <Button
              onClick={handleBulkSubmit}
              variant="contained"
              disabled={
                isLoading || isSubmitting || (isEleventhTwelfthClass
                  ? selectedGroups.length === 0
                  : selectedSubjects.length === 0
                ) || !selectedSession || !selectedClass || !selectedClassName || (selectedSession && !selectedSession.isActive)
              }
              sx={{
                minWidth: 120,
                backgroundColor: "#3092e3",
                "&:hover": {
                  backgroundColor: "primary.main",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#b2ebff",
                  opacity: 0.6,
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                (() => {
                  if (isEleventhTwelfthClass) {
                    const totalSubjects = selectedGroups.reduce((total, groupName) => {
                      const group = ELEVENTH_TWELFTH_CLASS_SUBJECTS_GROUPED.find(g => g.groupName === groupName);
                      if (group) {
                        const safeExistingSubjects = existingSubjects || [];
                        const availableSubjects = group.subjects.filter(
                          subject => !safeExistingSubjects.includes(subject.name)
                        );
                        return total + availableSubjects.length;
                      }
                      return total;
                    }, 0);
                    return t("create_subjects")?.replace("{count}", totalSubjects.toString()) || `Create ${totalSubjects} Subjects`;
                  } else {
                    return t("create_subjects")?.replace("{count}", selectedSubjects.length.toString()) || `Create ${selectedSubjects.length} Subjects`;
                  }
                })()
              )}
            </Button>

      {/* Bulk mapping editor */}
      <Dialog
        open={bulkMappingEditorOpen}
        onClose={() => setBulkMappingEditorOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {(t("edit_subject_marks_mapping") || "Edit Subject Marks Mapping") +
            (bulkEditing ? ` - ${bulkEditing.subjectName}` : "")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {bulkDraft ? (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Subject Marks Mapping
                </Typography>
              </Grid>

              {/* Row 1: Subject Type + Total Marks */}
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  margin="dense"
                  required
                  variant="outlined"
                  size="small"
                  sx={{ backgroundColor: "#f8f9fb" }}
                >
                  <InputLabel
                    sx={{
                      "& .MuiFormLabel-asterisk": { color: "red" },
                    }}
                  >
                    Subject Type
                  </InputLabel>
                  <Select
                    value={bulkDraft.subjectType || defaultSubjectCategory}
                    label="Subject Type"
                    onChange={(e) => handleBulkDraftField("subjectType", e.target.value as string)}
                    size="small"
                    input={<OutlinedInput label="Subject Type" size="small" />}
                    sx={{
                      minHeight: "40px",
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  >
                    {SUBJECT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Total Marks"
                  name="totalMarks"
                  value={bulkDraft.totalMarks}
                  onChange={(e) => handleBulkDraftField("totalMarks", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  error={!bulkMarksSumValid}
                  helperText={!bulkMarksSumValid ? "Theory Marks + Practical Marks must equal Total Marks." : " "}
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Row 2: Theory + Practical */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Theory Marks"
                  name="theoryMarks"
                  value={bulkDraft.theoryMarks}
                  onChange={(e) => handleBulkDraftField("theoryMarks", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  error={!bulkMarksSumValid}
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Practical Marks"
                  name="practicalMarks"
                  value={bulkDraft.practicalMarks}
                  onChange={(e) => handleBulkDraftField("practicalMarks", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  error={!bulkMarksSumValid}
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Row 3: Pass marks */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Theory Pass Marks"
                  name="thPassMarks"
                  value={bulkDraft.thPassMarks}
                  onChange={(e) => handleBulkDraftField("thPassMarks", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Practical Pass Marks"
                  name="prPassMarks"
                  value={bulkDraft.prPassMarks}
                  onChange={(e) => handleBulkDraftField("prPassMarks", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Row 4: E1 grades */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="E1 Grade Theory"
                  name="e1Th"
                  value={bulkDraft.e1Th}
                  onChange={(e) => handleBulkDraftField("e1Th", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="E1 Grade Practical"
                  name="e1Pr"
                  value={bulkDraft.e1Pr}
                  onChange={(e) => handleBulkDraftField("e1Pr", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Row 5: E2 grades */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="E2 Grade Theory"
                  name="e2Th"
                  value={bulkDraft.e2Th}
                  onChange={(e) => handleBulkDraftField("e2Th", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="E2 Grade Practical"
                  name="e2Pr"
                  value={bulkDraft.e2Pr}
                  onChange={(e) => handleBulkDraftField("e2Pr", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  required
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>

              {/* Grace toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={bulkDraft.isAllowedGrace}
                      onChange={(e) => handleBulkDraftField("isAllowedGrace", e.target.checked)}
                      name="isAllowedGrace"
                      color="primary"
                    />
                  }
                  label="Is Allowed Grace"
                />
              </Grid>
              {bulkDraft.isAllowedGrace ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Grace"
                    name="grace"
                    value={bulkDraft.grace}
                    onChange={(e) => handleBulkDraftField("grace", Number(sanitizeMarksDigitsInput(e.target.value) || 0))}
                    margin="dense"
                    size="small"
                    variant="outlined"
                    required
                    sx={{ backgroundColor: "#f8f9fb" }}
                  />
                </Grid>
              ) : null}
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkMappingEditorOpen(false)}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveBulkEditor()}
            disabled={!bulkMarksSumValid || savingBulkMapping}
          >
            {savingBulkMapping ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : (
              t("save") || "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: "#f44336", // red
                color: "#f44336",
                ml: 2,
                "&:hover": {
                  borderColor: "#b71c1c",
                  color: "#b71c1c",
                  backgroundColor: "rgba(244,67,54,0.08)",
                },
              }}
            >
              {t("cancel")}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={
                isLoading ||
                isSubmitting ||
                lockAllFields ||
                !formData.name ||
                !formData.subjectCode ||
                !formData.subjectCategory ||
                !mappingData.subjectType ||
                !marksSumValid ||
                !selectedSession ||
                !selectedClass ||
                (selectedSession && !selectedSession.isActive)
              }
              sx={{
                minWidth: 100,
                backgroundColor: "#3092e3",
                "&:hover": {
                  backgroundColor: "primary.main",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#b2ebff",
                  opacity: 0.6,
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                isEdit ? t("update") : t("create")
              )}
            </Button>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: "#f44336", // red
                color: "#f44336",
                ml: 2,
                "&:hover": {
                  borderColor: "#b71c1c",
                  color: "#b71c1c",
                  backgroundColor: "rgba(244,67,54,0.08)",
                },
              }}
            >
              {t("cancel")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubjectFormModal;